/**
 * Module dependencies.
 */

import { numericRule, requiredRule } from 'app/core/utils/field-rules';
import { usePaginatedCrudRequest } from 'app/hooks/app-requests/crud/use-paginated-crud-request';
import { useCrudRequest } from 'app/hooks/app-requests/crud/use-crud-request';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { Pharmacy } from 'app/types/pharmacy';
import { TFunction } from 'react-i18next';
import { SubTitle } from 'app/components/atoms/sub-title/sub-title';
import styles from '../sales-list.module.less';
import React from 'react';
import { FormListField } from 'app/components/organisms/crud/form-types';
import { SaleItemsForm } from './sale-items-form';

/**
 * Export `saleFormConfig` function.
 */

export const saleFormConfig = (translate: TFunction): FormListField[] => [
  {
    type: 'columns',
    name: 'mainWire',
    columns: [
      {
        column: 6,
        children: [
          {
            type: 'selectField',
            name: 'pharmacyId',
            showSearch: true,
            label: translate('users.sales.add.labels.pharmacy'),
            rules: requiredRule(translate),
            paginated: { enabled: true, pageSize: 50 },
            options: {
              hook: usePaginatedCrudRequest as any,
              hookProps: (_values: any, _params: any, options: any) => {
                const searchValue = options?.searchValue?.trim();
                return [
                  {
                    key: ['pharmacies-paginated', searchValue || ''],
                    endpoint: 'pharmacies' as any,
                    options: {
                      ...(searchValue ? { search: searchValue } : {})
                    },
                    pageSize: 50
                  }
                ];
              },
              normalize: (items: Pharmacy[]) => {
                if (!items || !Array.isArray(items)) {
                  return [];
                }
                return items
                  .filter(item => item && item.id)
                  .map(item => ({
                    value: item.id,
                    label: item.name,
                    searchValue: item.name
                  }));
              }
            }
          }
        ]
      },
      {
        column: 6,
        children: [
          {
            type: 'inputField',
            name: 'externalUserId',
            label: translate('users.sales.add.labels.externalUserId'),
            rules: requiredRule(translate)
          }
        ]
      },
      {
        column: 6,
        children: [
          {
            type: 'inputField',
            name: 'externalSaleId',
            label: translate('users.sales.add.labels.externalSaleId'),
            rules: requiredRule(translate)
          }
        ]
      },
      {
        column: 6,
        children: [
          {
            type: 'selectField',
            name: 'status',
            label: translate('users.sales.add.labels.status'),
            rules: requiredRule(translate),
            options: [
              {
                value: 'pending',
                label: String(translate('users.sales.add.labels.statusLabels.pending'))
              },
              {
                value: 'ready',
                label: String(translate('users.sales.add.labels.statusLabels.ready'))
              },
              {
                value: 'completed',
                label: String(translate('users.sales.add.labels.statusLabels.completed'))
              }
            ]
          }
        ]
      }
    ]
  },

  {
    type: 'columns',
    name: 'mainWire',
    columns: [
      {
        column: 8,
        children: [
          {
            type: 'inputField',
            name: 'vatNumber',
            label: translate('users.sales.add.labels.vatNumber'),
            rules: numericRule(translate)
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'selectField',
            name: 'provider',
            label: translate('users.sales.add.labels.provider'),
            rules: requiredRule(translate),
            options: [
              {
                value: 'integration',
                label: String(translate('users.sales.add.labels.providerLabels.integration'))
              },
              {
                value: 'backOffice',
                label: String(translate('users.sales.add.labels.providerLabels.backoffice'))
              },
              {
                value: 'mobile',
                label: String(translate('users.sales.add.labels.providerLabels.mobile'))
              }
            ]
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'if',
            name: 'isIntegration',
            watchFields: ['provider'],
            condition: ([provider]: any) => provider === 'integration',
            thenIf: [
              {
                type: 'selectField',
                name: 'integrationId',
                showSearch: true,
                label: translate('users.sales.add.labels.integration'),
                rules: requiredRule(translate),
                filterOption: (input: any, option: any) => {
                  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
                },
                options: {
                  hook: useCrudRequest as any,
                  hookProps: (values: any, params: any) => {
                    if (!values.pharmacyId) {
                      return [{}];
                    }

                    return [
                      {
                        key: ['integrations', values.pharmacyId, params.userId],
                        options: { interpolations: { pharmacyId: values.pharmacyId } },
                        endpoint: apiEndpoints.pharmaciesIntegrations
                      }
                    ];
                  },
                  normalize: (items: Pharmacy[]) => {
                    return (
                      items?.map(item => ({
                        value: item.id,
                        label: item.name,
                        searchValue: item.name
                      })) ?? []
                    );
                  }
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    type: 'checkBoxField',
    name: 'isAnnulled',
    label: translate('users.sales.add.labels.isAnnulled'),
    rules: undefined
  },
  {
    type: 'render',
    name: 'invalid_bank_account',
    render: (
      <div className={styles.orderItemHeader}>
        <SubTitle>{translate('users.sales.add.orderItems')}</SubTitle>
      </div>
    )
  },
  {
    type: 'wrapper',
    name: 'wrapper',
    wrapperComponent: SaleItemsForm,
    formFields: [
      /* inside component */
    ]
  }
];
